"use client";

import { useCallback } from "react";
import type { BuilderFeeApprovalMessage, HyperliquidApproveResult } from "@cede/utils";
import {
  APPROVE_BUILDER_FEE_PRIMARY,
  HYPERLIQUID_APPROVAL_DOMAIN,
  HYPERLIQUID_BUILDER_FEE_TYPES,
  HYPERLIQUID_CHAIN_MAINNET,
  HYPERLIQUID_DEFAULT_CHAIN_ID_HEX,
  postHyperliquidExchange,
  postHyperliquidInfo,
  splitSignature,
  HIP3_BUILDER_ADDRESS,
  HIP3_BUILDER_FEE_RATE,
  HIP3_BUILDER_FEE_INT,
} from "@cede/utils";
import { useSignTypedDataWithConnection } from "@/hooks/core/useSignTypedDataWithConnection";
import { isWalletActionError, WALLET_ACTION_ERROR_CODE } from "@/hooks/core/useWalletActionGuard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

type BuilderFeeApprovalOutcome = {
  mode: "approved" | "already-approved" | "confirmed-after-duplicate";
  result: Extract<HyperliquidApproveResult, { status: "ok" }>;
};

const builderFeeApprovalPromises = new Map<string, Promise<BuilderFeeApprovalOutcome>>();
const lastBuilderFeeNonceByAddress = new Map<string, number>();

function getBuilderFeeAddressKey(userAddress: string | undefined) {
  return userAddress?.toLowerCase();
}

function getNextBuilderFeeNonce(addressKey: string) {
  const now = Date.now();
  const previousNonce = lastBuilderFeeNonceByAddress.get(addressKey) ?? 0;
  const nextNonce = Math.max(now, previousNonce + 1);
  lastBuilderFeeNonceByAddress.set(addressKey, nextNonce);
  return nextNonce;
}

/**
 * Hook to check and approve the HIP-3 builder fee (2bps).
 *
 * ApproveBuilderFee is a one-time EIP-712 signature from the user's main wallet.
 * Once approved, the SDK automatically attaches the builder field to every HIP-3 order.
 * The same builder address is used for all HIP-3 platforms (TradeXYZ, Felix, Ventuals, etc.).
 */
export function useBuilderFeeApproval(userAddress: string | undefined) {
  const { signTypedDataAsync } = useSignTypedDataWithConnection();
  const queryClient = useQueryClient();
  const addressKey = getBuilderFeeAddressKey(userAddress);

  const queryKey = ["builder-fee-approval", addressKey, HIP3_BUILDER_ADDRESS] as const;

  const checkApprovalStatus = useCallback(async () => {
    if (!addressKey) {
      return false;
    }

    try {
      const result = await postHyperliquidInfo<number>({
        type: "maxBuilderFee",
        user: addressKey,
        builder: HIP3_BUILDER_ADDRESS,
      });
      return typeof result === "number" && result >= HIP3_BUILDER_FEE_INT;
    } catch {
      return false;
    }
  }, [addressKey]);

  // Check if builder fee is already approved via HL info API.
  // HL returns a number: 0 = not approved, >0 = max approved fee rate (e.g. 0.0002 for 2bps).
  const { data: isApproved = false, isLoading: isChecking } = useQuery({
    queryKey,
    queryFn: checkApprovalStatus,
    enabled: Boolean(addressKey),
    staleTime: Infinity, // Once approved, it never changes
  });

  // Sign and submit builder fee approval
  const { mutateAsync: approveBuilderFeeMutation, isPending: isApproving } = useMutation({
    mutationFn: async (): Promise<BuilderFeeApprovalOutcome> => {
      if (!addressKey) {
        throw new Error("Wallet not connected");
      }

      const alreadyApproved = await checkApprovalStatus();
      if (alreadyApproved) {
        queryClient.setQueryData(queryKey, true);
        return {
          mode: "already-approved",
          result: { status: "ok", response: { type: "default" } },
        };
      }

      const nonce = getNextBuilderFeeNonce(addressKey);

      const message: BuilderFeeApprovalMessage = {
        hyperliquidChain: HYPERLIQUID_CHAIN_MAINNET,
        maxFeeRate: HIP3_BUILDER_FEE_RATE,
        builder: HIP3_BUILDER_ADDRESS,
        nonce: BigInt(nonce),
      };

      // User signs with main wallet (EIP-712)
      // Uses connection-aware wrapper that reconnects if wallet was disconnected
      const signature = await signTypedDataAsync({
        domain: HYPERLIQUID_APPROVAL_DOMAIN,
        types: HYPERLIQUID_BUILDER_FEE_TYPES,
        primaryType: APPROVE_BUILDER_FEE_PRIMARY,
        message,
      });

      // Split signature into {r, s, v} components (HL API requires this format)
      const sigComponents = splitSignature(signature);

      // Submit directly to HL exchange API
      // signatureChainId must be INSIDE the action (HL Rust serde rejects unknown top-level fields)
      const action = {
        type: "approveBuilderFee",
        hyperliquidChain: HYPERLIQUID_CHAIN_MAINNET,
        signatureChainId: HYPERLIQUID_DEFAULT_CHAIN_ID_HEX,
        maxFeeRate: HIP3_BUILDER_FEE_RATE,
        builder: HIP3_BUILDER_ADDRESS,
        nonce,
      };

      const result = await postHyperliquidExchange<HyperliquidApproveResult>({
        action,
        nonce,
        signature: sigComponents,
      });

      if (result.status === "err") {
        const errorMessage = typeof result.response === "string" ? result.response : "Approval failed";
        const isDuplicateNonce = errorMessage.toLowerCase().includes("duplicate nonce");

        if (isDuplicateNonce) {
          const confirmedApproval = await checkApprovalStatus();
          if (confirmedApproval) {
            queryClient.setQueryData(queryKey, true);
            return {
              mode: "confirmed-after-duplicate",
              result: { status: "ok", response: { type: "default" } },
            };
          }
        }

        throw new Error(errorMessage);
      }

      return {
        mode: "approved",
        result,
      };
    },
    onSuccess: (outcome) => {
      queryClient.setQueryData(queryKey, true);
      if (outcome.mode === "approved") {
        enqueueSnackbar("Builder fee approved", { variant: "success" });
      }
    },
    onError: (error) => {
      if (isWalletActionError(error) && error.code === WALLET_ACTION_ERROR_CODE.USER_REJECTED) {
        return;
      }
      enqueueSnackbar(`Builder fee approval failed: ${error.message}`, { variant: "error" });
    },
  });

  const approveBuilderFee = useCallback(async () => {
    if (!addressKey) {
      throw new Error("Wallet not connected");
    }

    const existingApprovalPromise = builderFeeApprovalPromises.get(addressKey);
    if (existingApprovalPromise) {
      return existingApprovalPromise;
    }

    const approvalPromise = approveBuilderFeeMutation().finally(() => {
      if (builderFeeApprovalPromises.get(addressKey) === approvalPromise) {
        builderFeeApprovalPromises.delete(addressKey);
      }
    });

    builderFeeApprovalPromises.set(addressKey, approvalPromise);
    return approvalPromise;
  }, [addressKey, approveBuilderFeeMutation]);

  return {
    isApproved,
    isChecking,
    approveBuilderFee,
    isApproving,
  };
}
